import React, { useEffect, useRef, useState, useCallback } from 'react';
import CoinGeckoAttribution from './CoinGeckoAttribution';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { createChart, ColorType, CrosshairMode, LineStyle, IChartApi, ISeriesApi, CandlestickData, HistogramData, Time, LineData, SeriesMarker } from 'lightweight-charts';
import { getApiUrl } from '../utils/apiConfig';
import { authHeaders } from '../utils/role';
import { ChartFrame, Panel, TacticalButton } from './ui/TacticalPrimitives';
import ProvenanceFooter from './ProvenanceFooter';
import HowToRead from './HowToRead';
import { paletteFor, paletteRgba, PaletteToken, ThemeName } from '../theme/palette';
import DarkIsland from '../theme/DarkIsland';

interface PriceChartWidgetProps {
  asset: string;
  currentPrice?: number;
  change24h?: number;
  marketCap?: number;
  volume24h?: number;
  volumeBasis?: string; // trusted_cex_reconciled | cex_reported | coingecko_top_line
  totalReportedVolume?: number; // unfiltered total-reported 24h volume — context only
  lastUpdated?: string;
}

interface OHLCVCandle {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

// VaR envelope — the backend var-series (same historical method as the VaR cards). The chart
// draws these values as-is and never recomputes VaR.
interface VaRPoint {
  t: number;
  close: number;
  var95_lower: number; var95_upper: number;
  var99_lower: number; var99_upper: number;
  // Loss-side VaR exceptions (governing) + two-sided envelope breaches (diagnostic only).
  exception_95: boolean; exception_99: boolean;
  envelope_breach_95?: boolean; envelope_breach_99?: boolean;
}
interface VaRMeta {
  gate: string;        // PASS | INDICATIVE | WITHHELD
  reason: string;
  receipt: string;     // one-line legend/receipt under the chart
  runId: string;
}

// The VaR envelope is an END-OF-DAY (daily) band; it is only meaningful on daily-bar timeframes.
// Short timeframes render intraday bars (1D→30m, 7D→hourly, 1M→4h), where a daily band would
// mislead — so the envelope is shown only on 3M/6M/1Y (days ≥ 90). This matches the spec's
// "no intraday envelope" rule.
function isDailyTimeframe(days: string): boolean {
  return Number(days) >= 90;
}

const TIMEFRAMES = [
  { label: '1D', days: '1' },
  { label: '7D', days: '7' },
  { label: '1M', days: '30' },
  { label: '3M', days: '90' },
  { label: '6M', days: '180' },
  { label: '1Y', days: '365' },
] as const;

// Moving-average overlays. Values always come from the server (/api/chart in candle mode, the var-series
// moving_averages block in VaR mode — both on the same closes as the drawn price); the browser only draws them.
type MAKey = 'sma20' | 'sma50' | 'sma100' | 'sma200' | 'ema50';
const MA_LINES: { key: MAKey; label: string; period: number; tone: [PaletteToken, string]; defaultOn: boolean }[] = [
  { key: 'sma20', label: 'SMA 20', period: 20, tone: ['active', '0.8'], defaultOn: true },
  { key: 'sma50', label: 'SMA 50', period: 50, tone: ['hex-608cbe', '0.85'], defaultOn: true },
  { key: 'sma100', label: 'SMA 100', period: 100, tone: ['hex-789664', '0.85'], defaultOn: false },
  { key: 'sma200', label: 'SMA 200', period: 200, tone: ['gold', '0.9'], defaultOn: false },
  { key: 'ema50', label: 'EMA 50', period: 50, tone: ['warning', '0.72'], defaultOn: false },
];
// Theme step 2: chart colours are resolved for the ACTIVE theme (lightweight-charts takes literal colours, not var()).
const maColor = (m: { tone: [PaletteToken, string] }, theme: ThemeName) => paletteRgba(m.tone[0], m.tone[1], theme);
type MAPoint = { time: number; value: number };

// Chart-level colours for a theme — used at createChart and re-applied (applyOptions) when the theme changes.
function chartThemeOptions(th: ThemeName) {
  const pal = paletteFor(th);
  return {
    layout: { background: { type: ColorType.Solid, color: pal['surface'] }, textColor: pal['text-mute'] },
    grid: { vertLines: { color: paletteRgba('text-mute', '0.06', th) }, horzLines: { color: paletteRgba('text-mute', '0.06', th) } },
    crosshair: {
      vertLine: { color: paletteRgba('active', '0.35', th), labelBackgroundColor: pal['panel'] },
      horzLine: { color: paletteRgba('active', '0.35', th), labelBackgroundColor: pal['panel'] },
    },
    rightPriceScale: { borderColor: paletteRgba('border', '0.95', th) },
    timeScale: { borderColor: paletteRgba('border', '0.95', th) },
  };
}
const varBandFill = (dashed: boolean, th: ThemeName) => ({
  topColor: dashed ? paletteRgba('gold-muted', '0.05', th) : paletteRgba('gold-muted', '0.10', th),
  bottomColor: dashed ? paletteRgba('gold-muted', '0.05', th) : paletteRgba('gold-muted', '0.10', th),
});
const volumeData = (raw: OHLCVCandle[], th: ThemeName): HistogramData[] => raw.map((c) => ({
  time: c.time as Time,
  value: c.volume || Math.abs(c.close - c.open) * 1000,
  color: c.close >= c.open ? paletteRgba('safe', '0.22', th) : paletteRgba('critical', '0.22', th),
}));
// Loss-side VaR exceptions below the close (governing); an upper-tail breakout of the envelope is a separate,
// muted diagnostic marker above the close — it never counts as a VaR exception.
const varMarkers = (series: VaRPoint[], th: ThemeName): SeriesMarker<Time>[] =>
  series.filter((p) => p.exception_95 || (p.envelope_breach_99 && !p.exception_99)).map((p) => (p.exception_95 ? {
    time: p.t as Time,
    position: 'belowBar' as const,
    color: p.exception_99 ? paletteFor(th)['hex-c0392b'] : paletteRgba('active', '0.9', th),
    shape: (p.exception_99 ? 'circle' : 'square') as 'circle' | 'square',
  } : {
    time: p.t as Time,
    position: 'aboveBar' as const,
    color: paletteRgba('active', '0.55', th),
    shape: 'arrowUp' as const,
  }));

function computeSMA(data: { time: Time; close: number }[], period: number): LineData[] {
  const result: LineData[] = [];
  for (let i = period - 1; i < data.length; i++) {
    let sum = 0;
    for (let j = i - period + 1; j <= i; j++) {
      sum += data[j].close;
    }
    result.push({ time: data[i].time, value: sum / period });
  }
  return result;
}

function computeEMA(data: { time: Time; close: number }[], period: number): LineData[] {
  if (data.length < period) return [];
  const k = 2 / (period + 1);
  let sum = 0;
  for (let i = 0; i < period; i++) sum += data[i].close;
  let ema = sum / period;
  const result: LineData[] = [{ time: data[period - 1].time, value: ema }];
  for (let i = period; i < data.length; i++) {
    ema = data[i].close * k + ema * (1 - k);
    result.push({ time: data[i].time, value: ema });
  }
  return result;
}

const fmtVolume = (v: number) => (v >= 1e9 ? `$${(v / 1e9).toFixed(2)}B` : v >= 1e6 ? `$${(v / 1e6).toFixed(2)}M` : `$${(v / 1e3).toFixed(2)}K`);

// The 24h volume label names its basis (owner 2026-09-28): a filtered trusted-venue figure must never read as total
// market volume. Renamed to APPROVED-VENUE VOLUME once the ApprovedVenueRegistry is ratified.
function volumeBasisLabel(basis?: string): { label: string; tip: string } {
  switch (basis) {
    case 'trusted_cex_reconciled':
      return { label: 'Trust-Filtered CEX Volume · 24H', tip: 'Centralized venues filtered by CoinGecko trust score (green) or a listed-exchange match — a provider-based filter, not an SAA-approved venue basket. Same ticker sample and 24h window as the total reported volume. Not total market volume.' };
    case 'cex_reported':
      return { label: 'CEX-Reported Volume · 24H', tip: 'Reported volume across centralized venues (DEX excluded); trusted-venue decomposition is unavailable. This is not total reported market volume.' };
    case 'coingecko_top_line':
      return { label: 'Total Reported Volume · 24H', tip: 'Unfiltered total-reported aggregate volume; not a trusted-venue or executable-liquidity measure.' };
    default:
      return { label: 'Reported Volume · 24H · basis unstated', tip: 'The volume basis was not reported for this run.' };
  }
}

const PriceChartWidget: React.FC<PriceChartWidgetProps> = ({
  asset,
  currentPrice,
  change24h,
  marketCap,
  volume24h,
  volumeBasis,
  totalReportedVolume,
  lastUpdated
}) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  // Theme step 2: the active theme drives every chart colour; a change re-applies them (no re-create, no re-fetch).
  // Owner 2026-10-05: the Analysis price chart keeps its original dark look in every theme (never white).
  const theme: ThemeName = 'dark';
  const themeRef = useRef<ThemeName>(theme);
  themeRef.current = theme;
  const volumeBarsRef = useRef<OHLCVCandle[]>([]); // last candle-mode bars (volume colours are per bar)
  const varMarkSeriesRef = useRef<VaRPoint[]>([]); // last VaR-mode series (marker colours)
  const varDashedRef = useRef(false);
  const chartRef = useRef<IChartApi | null>(null);
  const candleSeriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
  const volumeSeriesRef = useRef<ISeriesApi<'Histogram'> | null>(null);
  const maSeriesRef = useRef<Partial<Record<MAKey, ISeriesApi<'Line'>>>>({});
  const maDataRef = useRef<Partial<Record<MAKey, LineData[]>>>({});
  // VaR envelope series (drawn from the backend var-series — the renderer NEVER recomputes VaR).
  const varFillUpperRef = useRef<ISeriesApi<'Area'> | null>(null);
  const varFillLowerRef = useRef<ISeriesApi<'Area'> | null>(null);
  const var99UpperRef = useRef<ISeriesApi<'Line'> | null>(null);
  const var99LowerRef = useRef<ISeriesApi<'Line'> | null>(null);
  // Daily close LINE used in VaR mode: the envelope, markers and price share ONE daily source
  // (the var-series), so nothing can misalign. Candles (from /api/chart) are used otherwise.
  const varCloseRef = useRef<ISeriesApi<'Line'> | null>(null);
  const varByTimeRef = useRef<Record<number, VaRPoint>>({});
  const varSeriesRef = useRef<VaRPoint[]>([]); // loaded once per run; the buttons only pan the view
  // Default to 3M (days=90) — a daily timeframe — so the EOD VaR envelope is overlaid on load
  // (showVaR defaults true). The band only draws on the daily timeframes (3M/6M/1Y, days>=90);
  // shorter timeframes render intraday bars and hide it. Owner directive: every asset should open
  // straight into the 3M chart with VaR already applied.
  const [timeframe, setTimeframe] = useState('90');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showMA, setShowMA] = useState<Record<MAKey, boolean>>(() => Object.fromEntries(MA_LINES.map((m) => [m.key, m.defaultOn])) as Record<MAKey, boolean>);
  const showMARef = useRef(showMA);
  showMARef.current = showMA;
  // Bars available when a line cannot be drawn (period longer than the loaded history) — shown on the button.
  const [maShort, setMaShort] = useState<Partial<Record<MAKey, number>>>({});
  const [showVaR, setShowVaR] = useState(true);
  const [varMeta, setVarMeta] = useState<VaRMeta | null>(null);
  const [varInView, setVarInView] = useState<string | null>(null); // viewport-scoped breach count (labelled)
  const timeframeRef = useRef(timeframe);
  timeframeRef.current = timeframe; // always current, so the fetch effect can read it without re-running on daily↔daily
  // Data identity: 'var' is the SAME for all daily timeframes (3M/6M/1Y) — switching among them
  // pans the viewport but does NOT re-fetch. Intraday timeframes each key their own candle fetch.
  const varDataMode = isDailyTimeframe(timeframe) && showVaR ? 'var' : `candle:${timeframe}`;
  const [legendData, setLegendData] = useState<{ o: number; h: number; l: number; c: number; v: number; time: string; var95l?: number; var95u?: number; var99l?: number; var99u?: number } | null>(null);
  const [lastCloseFromChart, setLastCloseFromChart] = useState<number | null>(null);
  // When and what the chart's last point is, so it is never read as the live spot: in VaR mode it is the last
  // COMPLETED UTC daily close (the forming day is excluded); in candle mode it is the last bar of the timeframe.
  const [lastChartPoint, setLastChartPoint] = useState<{ time: number; eod: boolean } | null>(null);

  const formatPrice = useCallback((p: number) => {
    if (p >= 1) return p.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (p >= 0.01) return p.toFixed(4);
    return p.toFixed(8);
  }, []);

  useEffect(() => {
    if (!chartContainerRef.current) return;
    const container = chartContainerRef.current;

    const chart = createChart(container, {
      width: container.clientWidth,
      height: 520,
      layout: {
        background: { type: ColorType.Solid, color: paletteFor(themeRef.current)['surface'] },
        textColor: paletteFor(themeRef.current)['text-mute'],
        fontSize: 12,
        fontFamily: 'JetBrains Mono, ui-monospace, SFMono-Regular, Menlo, Monaco, monospace',
      },
      grid: {
        vertLines: { color: paletteRgba('text-mute', '0.06', themeRef.current) },
        horzLines: { color: paletteRgba('text-mute', '0.06', themeRef.current) },
      },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: { color: paletteRgba('active', '0.35', themeRef.current), width: 1, style: 2, labelBackgroundColor: paletteFor(themeRef.current)['panel'] },
        horzLine: { color: paletteRgba('active', '0.35', themeRef.current), width: 1, style: 2, labelBackgroundColor: paletteFor(themeRef.current)['panel'] },
      },
      rightPriceScale: {
        borderColor: paletteRgba('border', '0.95', themeRef.current),
        scaleMargins: { top: 0.1, bottom: 0.25 },
      },
      timeScale: {
        borderColor: paletteRgba('border', '0.95', themeRef.current),
        timeVisible: true,
        secondsVisible: false,
        rightOffset: 5,
        barSpacing: 8,
      },
      // Pin axis/crosshair date formatting to en-US so the time axis never
      // renders in the browser's locale (e.g. Russian "июнь").
      localization: {
        locale: 'en-US',
        dateFormat: 'yyyy-MM-dd',
      },
      handleScroll: true,
      handleScale: true,
    });

    // VaR envelope series — created BEFORE candles so candles draw on top of the band. Values
    // come from the backend var-series; this widget never recomputes VaR. Band via an area-mask
    // pair (upper = gold fill down to baseline, lower = chart-bg mask), per the spec's v1 note.
    const varFillUpper = chart.addAreaSeries({
      topColor: paletteRgba('gold-muted', '0.10', themeRef.current), bottomColor: paletteRgba('gold-muted', '0.10', themeRef.current),
      lineColor: paletteRgba('gold-muted', '0.55', themeRef.current), lineWidth: 1,
      priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false,
    });
    const varFillLower = chart.addAreaSeries({
      topColor: paletteFor(themeRef.current)['surface'], bottomColor: paletteFor(themeRef.current)['surface'],
      lineColor: paletteRgba('gold-muted', '0.55', themeRef.current), lineWidth: 1,
      priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false,
    });
    const var99Upper = chart.addLineSeries({
      color: paletteRgba('gold-muted', '0.32', themeRef.current), lineWidth: 1, lineStyle: LineStyle.Dashed,
      priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false,
    });
    const var99Lower = chart.addLineSeries({
      color: paletteRgba('gold-muted', '0.32', themeRef.current), lineWidth: 1, lineStyle: LineStyle.Dashed,
      priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false,
    });

    // Daily close line for VaR mode (drawn on top of the envelope). Same daily times as the
    // envelope, so breach markers placed here always land on a real point.
    const varClose = chart.addLineSeries({
      color: paletteRgba('slate-200', '0.9', themeRef.current), lineWidth: 2,
      priceLineVisible: false, lastValueVisible: true, crosshairMarkerVisible: true,
    });

    const candleSeries = chart.addCandlestickSeries({
      upColor: paletteFor(themeRef.current)['safe'],
      downColor: paletteFor(themeRef.current)['critical'],
      borderDownColor: paletteFor(themeRef.current)['critical'],
      borderUpColor: paletteFor(themeRef.current)['safe'],
      wickDownColor: paletteFor(themeRef.current)['critical'],
      wickUpColor: paletteFor(themeRef.current)['safe'],
    });

    const volumeSeries = chart.addHistogramSeries({
      priceFormat: { type: 'volume' },
      priceScaleId: 'volume',
    });
    chart.priceScale('volume').applyOptions({
      scaleMargins: { top: 0.8, bottom: 0 },
    });

    const maSeries: Partial<Record<MAKey, ISeriesApi<'Line'>>> = {};
    for (const m of MA_LINES) {
      maSeries[m.key] = chart.addLineSeries({
        color: maColor(m, themeRef.current),
        lineWidth: 1,
        lineStyle: m.key === 'ema50' ? LineStyle.Dashed : LineStyle.Solid,
        title: m.label,
        crosshairMarkerVisible: false,
        priceLineVisible: false,
        lastValueVisible: false,
      });
    }

    chartRef.current = chart;
    candleSeriesRef.current = candleSeries;
    volumeSeriesRef.current = volumeSeries;
    maSeriesRef.current = maSeries;
    varFillUpperRef.current = varFillUpper;
    varFillLowerRef.current = varFillLower;
    var99UpperRef.current = var99Upper;
    var99LowerRef.current = var99Lower;
    varCloseRef.current = varClose;

    chart.subscribeCrosshairMove((param) => {
      if (!param || !param.time || !param.seriesData) {
        setLegendData(null);
        return;
      }
      const candle = param.seriesData.get(candleSeries) as CandlestickData | undefined;
      // In VaR mode there are no candles — read the close from the daily close line instead.
      const lineVal = param.seriesData.get(varClose) as LineData | undefined;
      if (candle || (lineVal && typeof lineVal.value === 'number')) {
        const t = param.time;
        let timeStr = '';
        if (typeof t === 'number') {
          timeStr = new Date(t * 1000).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });
        } else if (typeof t === 'object' && 'year' in t) {
          timeStr = `${t.year}-${String(t.month).padStart(2, '0')}-${String(t.day).padStart(2, '0')}`;
        }
        const c = candle ? candle.close : (lineVal!.value as number);
        const o = candle ? candle.open : c;
        const h = candle ? candle.high : c;
        const l = candle ? candle.low : c;
        const vp = typeof t === 'number' ? varByTimeRef.current[t] : undefined;
        setLegendData({
          o, h, l, c, v: 0, time: timeStr,
          var95l: vp?.var95_lower, var95u: vp?.var95_upper,
          var99l: vp?.var99_lower, var99u: vp?.var99_upper,
        });
      } else {
        setLegendData(null);
      }
    });

    // `disposed` guards against a ResizeObserver entry that the browser may still
    // deliver after disconnect() (and against React StrictMode's mount→unmount→mount
    // in dev): calling applyOptions on a removed chart re-schedules a paint on a
    // disposed canvas → "Object is disposed". The flag + try/catch stop that.
    let disposed = false;
    const resizeObserver = new ResizeObserver((entries) => {
      if (disposed || !chartRef.current) return;
      for (const entry of entries) {
        try {
          chart.applyOptions({ width: entry.contentRect.width });
        } catch {
          // chart disposed mid-resize during teardown/remount — ignore
        }
      }
    });
    resizeObserver.observe(container);

    return () => {
      disposed = true;
      resizeObserver.disconnect();
      try {
        chart.remove();
      } catch {
        // already removed
      }
      // Null ALL refs — the series belong to the removed chart; a late
      // applyChartData() (data for a previously-selected asset) must not call
      // setData() on a disposed series.
      chartRef.current = null;
      candleSeriesRef.current = null;
      volumeSeriesRef.current = null;
      maSeriesRef.current = {};
      varFillUpperRef.current = null;
      varFillLowerRef.current = null;
      var99UpperRef.current = null;
      var99LowerRef.current = null;
      varCloseRef.current = null;
    };
  }, []);

  // Theme change → re-apply every colour on the live chart (lightweight-charts takes literal colours).
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    const pal = paletteFor(theme);
    try {
      chart.applyOptions(chartThemeOptions(theme));
      varFillUpperRef.current?.applyOptions({ ...varBandFill(varDashedRef.current, theme), lineColor: paletteRgba('gold-muted', '0.55', theme) });
      varFillLowerRef.current?.applyOptions({ topColor: pal['surface'], bottomColor: pal['surface'], lineColor: paletteRgba('gold-muted', '0.55', theme) });
      var99UpperRef.current?.applyOptions({ color: paletteRgba('gold-muted', '0.32', theme) });
      var99LowerRef.current?.applyOptions({ color: paletteRgba('gold-muted', '0.32', theme) });
      varCloseRef.current?.applyOptions({ color: paletteRgba('slate-200', '0.9', theme) });
      candleSeriesRef.current?.applyOptions({
        upColor: pal['safe'], downColor: pal['critical'], borderDownColor: pal['critical'],
        borderUpColor: pal['safe'], wickDownColor: pal['critical'], wickUpColor: pal['safe'],
      });
      for (const m of MA_LINES) maSeriesRef.current[m.key]?.applyOptions({ color: maColor(m, theme) });
      if (volumeBarsRef.current.length) volumeSeriesRef.current?.setData(volumeData(volumeBarsRef.current, theme));
      if (varMarkSeriesRef.current.length) varCloseRef.current?.setMarkers(varMarkers(varMarkSeriesRef.current, theme));
    } catch {
      // chart disposed mid-switch — the next mount reads the active theme
    }
  }, [theme]);

  // Clear every VaR overlay series (envelope + daily close line + markers).
  const clearVaR = useCallback(() => {
    varMarkSeriesRef.current = [];
    varByTimeRef.current = {};
    varSeriesRef.current = [];
    setVarInView(null);
    varFillUpperRef.current?.setData([]);
    varFillLowerRef.current?.setData([]);
    var99UpperRef.current?.setData([]);
    var99LowerRef.current?.setData([]);
    varCloseRef.current?.setData([]);
    varCloseRef.current?.setMarkers([]);
  }, []);

  // Draw the stored MA data for the lines that are switched on; a toggle never re-fetches.
  const renderMA = useCallback(() => {
    for (const m of MA_LINES) {
      maSeriesRef.current[m.key]?.setData(showMARef.current[m.key] ? (maDataRef.current[m.key] || []) : []);
    }
  }, []);
  // Store one mode's MA series (server points) and note which lines lack enough history.
  const setMAData = useCallback((pick: (k: MAKey) => MAPoint[] | undefined, bars: number) => {
    const short: Partial<Record<MAKey, number>> = {};
    for (const m of MA_LINES) {
      const pts = pick(m.key) || [];
      maDataRef.current[m.key] = pts.map((p) => ({ time: p.time as Time, value: p.value }));
      if (pts.length === 0) short[m.key] = bars;
    }
    setMaShort(short);
    renderMA();
  }, [renderMA]);
  useEffect(() => { renderMA(); }, [showMA, renderMA]);

  // Candle mode: OHLC candles + volume + SMA/EMA from /api/chart. Hides the VaR close line.
  const applyChartData = useCallback((raw: OHLCVCandle[], server?: Partial<Record<MAKey, MAPoint[]>>) => {
    // Skip if the chart was disposed (e.g. data for a prior asset arrived after the
    // widget remounted) — setData on a removed series throws "Object is disposed".
    if (!chartRef.current || !candleSeriesRef.current) return;
    setLastCloseFromChart(raw.length > 0 ? raw[raw.length - 1].close : null);
    setLastChartPoint(raw.length > 0 ? { time: Number(raw[raw.length - 1].time), eod: false } : null);
    const candles: CandlestickData[] = raw.map((c) => ({
      time: c.time as Time, open: c.open, high: c.high, low: c.low, close: c.close,
    }));
    volumeBarsRef.current = raw;
    candleSeriesRef.current?.setData(candles);
    volumeSeriesRef.current?.setData(volumeData(raw, themeRef.current));
    // Desk parity: the SMA/EMA come SERVER-SIDE from /api/chart so no formula runs in the browser.
    // SMA 20 / EMA 50 fall back to the client computation only when an older payload lacks them.
    const closeData = raw.map((c) => ({ time: c.time as Time, close: c.close }));
    setMAData((k) => {
      const v = server?.[k];
      if (Array.isArray(v) && v.length > 0) return v;
      if (k === 'sma20' && closeData.length >= 20) return computeSMA(closeData, 20).map((p) => ({ time: Number(p.time), value: p.value }));
      if (k === 'ema50' && closeData.length >= 50) return computeEMA(closeData, 50).map((p) => ({ time: Number(p.time), value: p.value }));
      return [];
    }, raw.length);
    varCloseRef.current?.setData([]); // candle mode → no VaR close line
    chartRef.current?.timeScale().fitContent();
  }, [setMAData]);

  // VaR mode: the daily close LINE + envelope + breach markers, ALL from the var-series (one daily
  // source), so nothing can misalign. Candles/volume are cleared. Markers sit on the close line
  // (identical times) so they always land on a real point.
  const applyVaRMode = useCallback((series: VaRPoint[], gate: string, ma?: Record<string, { points?: MAPoint[]; history_bars?: number }>) => {
    if (!chartRef.current || !varCloseRef.current) return;
    const dashed = gate === 'INDICATIVE';
    varDashedRef.current = dashed;
    varMarkSeriesRef.current = series;
    volumeBarsRef.current = [];
    candleSeriesRef.current?.setData([]);
    candleSeriesRef.current?.setMarkers([]);
    volumeSeriesRef.current?.setData([]);
    // MA overlays on the SAME daily closes as the close line (server moving_averages block).
    const hist = Number(ma?.sma20?.history_bars ?? series.length);
    setMAData((k) => ma?.[k]?.points, hist);
    setLastCloseFromChart(series.length ? series[series.length - 1].close : null);
    setLastChartPoint(series.length ? { time: Number(series[series.length - 1].t), eod: true } : null);
    varByTimeRef.current = Object.fromEntries(series.map((p) => [p.t, p]));
    varFillUpperRef.current?.applyOptions({
      lineStyle: dashed ? LineStyle.Dashed : LineStyle.Solid,
      ...varBandFill(dashed, themeRef.current),
    });
    varFillLowerRef.current?.applyOptions({ lineStyle: dashed ? LineStyle.Dashed : LineStyle.Solid });
    varFillUpperRef.current?.setData(series.map((p) => ({ time: p.t as Time, value: p.var95_upper })));
    varFillLowerRef.current?.setData(series.map((p) => ({ time: p.t as Time, value: p.var95_lower })));
    var99UpperRef.current?.setData(series.map((p) => ({ time: p.t as Time, value: p.var99_upper })));
    var99LowerRef.current?.setData(series.map((p) => ({ time: p.t as Time, value: p.var99_lower })));
    varCloseRef.current.setData(series.map((p) => ({ time: p.t as Time, value: p.close })));
    // Loss-side VaR exceptions below the close (governing); an upper-tail breakout of the envelope is a separate,
    // muted diagnostic marker above the close — it never counts as a VaR exception.
    varCloseRef.current.setMarkers(varMarkers(series, themeRef.current));
    chartRef.current?.timeScale().fitContent();
  }, [setMAData]);

  // Pan the VaR viewport (client-side only — the run's numbers never change). Also emits a
  // viewport-scoped breach count as a SEPARATE labelled line (never replacing the run receipt;
  // no Kupiec on a viewport).
  const applyViewport = useCallback((days: number) => {
    const s = varSeriesRef.current;
    const ts = chartRef.current?.timeScale();
    if (!ts || s.length === 0) return;
    const last = s[s.length - 1].t;
    const first = s[0].t;
    const spanAll = days >= 365 || last - first <= days * 86400;
    if (spanAll) {
      ts.fitContent();
    } else {
      ts.setVisibleRange({ from: (last - days * 86400) as Time, to: last as Time });
    }
    const fromT = spanAll ? first : last - days * 86400;
    const inView = s.filter((p) => p.t >= fromT);
    const b99 = inView.filter((p) => p.exception_99).length;
    setVarInView(`in view (${spanAll ? '1Y' : days + 'd'}): 99% loss-side VaR exceptions ${b99}/${inView.length} vs ${(0.01 * inView.length).toFixed(1)} expected`);
  }, []);

  // Re-pan when the timeframe changes in VaR mode — no re-fetch, just move the visible range.
  useEffect(() => {
    if (isDailyTimeframe(timeframe) && showVaR) applyViewport(Number(timeframe));
  }, [timeframe, showVaR, applyViewport]);

  // Single data effect. VaR mode (daily timeframe + toggle on, gate not WITHHELD) draws the daily
  // close line + envelope from the var-series (one source). Otherwise — or on WITHHELD/failure —
  // it draws candles from /api/chart. One owner, no cross-effect races.
  useEffect(() => {
    if (!asset || !candleSeriesRef.current) return;
    const wantVaR = isDailyTimeframe(timeframe) && showVaR;
    let cancelled = false;
    setLoading(true);
    setError(null);

    setVarInView(null);
    const loadCandles = () => {
      fetch(getApiUrl(`/api/chart/${encodeURIComponent(asset.toLowerCase())}?days=${timeframeRef.current}`), { headers: authHeaders() })
        .then((r) => r.json())
        .then((json) => {
          if (cancelled) return;
          if (!json.success || !Array.isArray(json.data) || json.data.length === 0) {
            // Fail closed: never synthesise OHLC — an explicit "unavailable" message, not fake candles.
            applyChartData([]);
            setError(json.error || 'Market data unavailable for this asset/timeframe — no chart shown.');
            return;
          }
          applyChartData(json.data as OHLCVCandle[], { sma20: json.sma20, sma50: json.sma50, sma100: json.sma100, sma200: json.sma200, ema50: json.ema50 });
        })
        .catch(() => { if (!cancelled) { setLastCloseFromChart(null); setError('Failed to load chart data'); } })
        .finally(() => { if (!cancelled) setLoading(false); });
    };

    if (!wantVaR) {
      clearVaR();
      setVarMeta(null);
      loadCandles();
      return () => { cancelled = true; };
    }

    // The calibration window is a RUN parameter (fixed 364d), NOT the viewport. The 3M/6M/1Y
    // buttons only pan the visible range (client-side); they never re-fetch or recompute — one
    // run_id ⇒ one set of numbers, whatever the viewport.
    fetch(getApiUrl(`/api/assets/${encodeURIComponent(asset.toLowerCase())}/var-series?window=364`))
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (cancelled) return;
        if (!j || !j.success) { setVarMeta(null); clearVaR(); loadCandles(); return; }
        const gate: string = j.quality_gate || 'PASS';
        const series: VaRPoint[] = Array.isArray(j.series) ? j.series : [];
        const bt = j.backtest || {};
        // A rejecting p-value (e.g. 0.000226) must NOT print as "0.00" — that reads as an exact zero and
        // hides that the test actually ran and decisively rejected. Show small p as "<0.001" (or 2 sig figs).
        const fmtP = (v: number): string => v >= 0.001 ? v.toFixed(3) : (v > 0 ? '<0.001' : '0');
        const p99 = bt.kupiec_99_pvalue != null ? fmtP(Number(bt.kupiec_99_pvalue)) : '—';
        const obs99 = bt.breaches_99?.observed ?? 0;
        const nEff = bt.n ?? series.length;
        const exp99 = bt.breaches_99?.expected != null ? Number(bt.breaches_99.expected).toFixed(1) : '—';
        // Current 1-day forecast = the exact metric-card VaR (same run), so the historical
        // no-look-ahead bands and the card are not misread as two diverging surfaces.
        const cur99 = j.current?.var99_1d_pct != null ? `${Number(j.current.var99_1d_pct).toFixed(2)}%` : '—';
        // "window 364d · n=NNN effective" — the warm start drops the first ~30 days.
        let receipt = `Chart-window VaR99 diagnostic (not the governing run validation) · historical empirical · window ${j.window_days}d · n=${nEff} effective · 1d horizon · run ${String(j.run_id || '').slice(0, 16)} · 99% loss-side VaR exceptions: ${obs99}/${nEff} vs ${exp99} expected · ${bt.governance_label || bt.traffic_light || 'N/A'} (Kupiec p=${p99}${bt.traffic_light && bt.traffic_light !== 'GREEN' ? ', raw Kupiec rejection: yes' : ''}) · current 1d VaR99 ${cur99} (= metric card)`;
        // var_coverage_directional_gate_v2: the adverse-direction note fires only for an under-coverage rejection.
        // The adverse note is stated only when exceptions actually exceed the expected count (never on over-coverage).
        const obsN = Number(bt.breaches_99?.observed ?? 0), expN = Number(bt.breaches_99?.expected ?? 0);
        if ((bt.governance_signal || bt.traffic_light) === 'RED' && obsN > expN) {
          // Kupiec is an unconditional-COVERAGE test: it shows the exception rate exceeds nominal. It says
          // nothing about clustering (Christoffersen independence) or a regime shift — neither is claimed.
          receipt += ' — exception rate materially exceeds the nominal 1% coverage (Kupiec unconditional-coverage test; clustering / regime not tested here)';
        }
        setVarMeta({ gate, reason: j.gate_reason || '', receipt, runId: j.run_id || '' });
        varSeriesRef.current = series;

        if (gate === 'WITHHELD' || series.length === 0) {
          clearVaR();     // no envelope on WITHHELD
          loadCandles();  // still show price (candles) + the WITHHELD banner
          return;
        }
        applyVaRMode(series, gate, j.moving_averages);
        applyViewport(Number(timeframeRef.current)); // pan to the current timeframe's window
        setLoading(false);
      })
      .catch(() => { if (!cancelled) { setVarMeta(null); clearVaR(); loadCandles(); } });
    return () => { cancelled = true; };
  }, [asset, varDataMode, showVaR, currentPrice, applyChartData, applyVaRMode, applyViewport, clearVaR]);

  if (!asset) return null;

  const isPositive = (change24h || 0) >= 0;

  return (
    <section className="py-8 md:py-10 px-4 md:px-10 lg:px-20 bg-[color:var(--c-panel-black)]">
      <div className="max-w-[1600px] mx-auto">
        <ChartFrame title={`${asset.toUpperCase()} Price Chart`} subtitle="OHLC candlestick / volume / SMA 20·50·100·200 / EMA 50 / VaR envelope (EOD, 3M+)">
          <div className="flex flex-col gap-4 mb-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <div className="section-heading mb-1">Market Structure</div>
              <h2 className="text-2xl md:text-3xl font-semibold text-cmd-text mb-2 metric-value">
                {asset.toUpperCase()} / OHLC candlestick
              </h2>
              <p className="text-cmd-textDim text-sm font-mono">Bars = selected timeframe</p>
            </div>
            {(() => {
              // One headline price: the live spot used everywhere downstream (24h
              // change, market cap). The chart's last candle close is shown on the
              // chart and crosshair legend, not as a second competing number.
              const hasLive = currentPrice != null && currentPrice > 0;
              const headline = hasLive ? currentPrice! : lastCloseFromChart;
              if (headline == null) return null;
              const headlineLabel = hasLive ? 'Spot · CoinGecko' : 'Last close · chart';
              return (
                <div className="text-left sm:text-right">
                  <div className="text-3xl md:text-4xl font-bold text-cmd-text font-mono tabular-nums mb-1">
                    ${formatPrice(headline)}
                  </div>
                  <div
                    className="text-[10px] text-cmd-textDim font-mono uppercase tracking-[0.08em] mb-1"
                    title="Live spot price from CoinGecko — the same value used for the 24h change and market cap below. The chart ends at its own last point (dated below), so it can differ from the live spot."
                  >
                    {headlineLabel}
                  </div>
                  {hasLive && lastCloseFromChart != null && lastChartPoint && Number.isFinite(lastChartPoint.time) && (
                    <div
                      className="text-[11px] text-cmd-textDim font-mono tabular-nums mb-1"
                      data-testid="chart-last-point"
                      title={lastChartPoint.eod
                        ? 'The chart line ends at the last COMPLETED UTC daily close; the current day is still forming and is not drawn. The headline above is the live spot, so the two differ by the move since that close.'
                        : 'The last bar of the selected timeframe (it may still be forming). The headline above is the live spot.'}
                    >
                      {lastChartPoint.eod ? 'Chart: last daily close' : 'Chart: last bar'} ${formatPrice(lastCloseFromChart)} · {new Date(lastChartPoint.time * 1000).toISOString().slice(0, lastChartPoint.eod ? 10 : 16).replace('T', ' ')} UTC
                    </div>
                  )}
                  {/* P0-17: a real number renders the colored move; a missing value (feed down) renders
                      a dim "— (24h)" — NEVER a green +0.00% next to a ticker showing a real change. */}
                  {Number.isFinite(change24h as number) ? (
                    <div className={`flex items-center sm:justify-end gap-2 text-xl font-semibold ${isPositive ? 'text-cmd-safe' : 'text-cmd-critical'}`}>
                      {isPositive ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />}
                      <span className="tabular-nums">{isPositive ? '+' : ''}{(change24h as number).toFixed(2)}% (24h)</span>
                    </div>
                  ) : (
                    <div className="flex items-center sm:justify-end gap-2 text-xl font-semibold text-cmd-textDim" title="24h change unavailable — the price feed did not return a value for this asset. Shown as '—' rather than a defaulted 0.00%.">
                      <span className="tabular-nums">— (24h)</span>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>

          {/* Stats row */}
          {(marketCap || volume24h) && (
            <div className="flex gap-4 mb-4">
              {marketCap != null && marketCap > 0 && (
                <Panel className="px-4 py-2">
                  <div className="text-[10px] text-cmd-textDim font-sans uppercase tracking-[0.14em] mb-1 font-medium">Market Cap</div>
                  <div className="text-lg font-semibold text-cmd-text font-mono tabular-nums">
                    {marketCap >= 1e12 ? `$${(marketCap / 1e12).toFixed(2)}T` : marketCap >= 1e9 ? `$${(marketCap / 1e9).toFixed(2)}B` : marketCap >= 1e6 ? `$${(marketCap / 1e6).toFixed(2)}M` : `$${(marketCap / 1e3).toFixed(2)}K`}
                  </div>
                </Panel>
              )}
              {volume24h != null && volume24h > 0 && (
                <Panel className="px-4 py-2">
                  <div className="text-[10px] text-cmd-textDim font-sans uppercase tracking-[0.14em] mb-1 font-medium" title={volumeBasisLabel(volumeBasis).tip}>{volumeBasisLabel(volumeBasis).label}</div>
                  <div className="text-lg font-semibold text-cmd-text font-mono tabular-nums" title={volumeBasisLabel(volumeBasis).tip}>
                    {fmtVolume(volume24h)}
                  </div>
                  {volumeBasis !== 'coingecko_top_line' && totalReportedVolume != null && totalReportedVolume > 0 && Math.abs(totalReportedVolume - volume24h) / volume24h > 0.01 && (
                    <div className="mt-0.5 text-[10px] text-cmd-textDim font-mono tabular-nums" title="Unfiltered total-reported 24h volume across all listed venues (market-data aggregate). Context only — not a trusted-venue or executable-liquidity measure.">
                      Total reported · context only · {fmtVolume(totalReportedVolume)}
                    </div>
                  )}
                </Panel>
              )}
            </div>
          )}

          {/* Timeframe + indicator toggles */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {TIMEFRAMES.map((tf) => (
              <TacticalButton
                key={tf.days}
                onClick={() => setTimeframe(tf.days)}
                className="font-mono"
                size="sm"
                variant={timeframe === tf.days ? 'primary' : 'secondary'}
              >
                {tf.label}
              </TacticalButton>
            ))}
            <div className="w-px h-6 bg-[color:rgb(var(--c-border-rgb)/0.95)] mx-2" />
            {MA_LINES.map((m) => {
              const short = maShort[m.key];
              const unit = isDailyTimeframe(timeframe) ? 'daily closes' : 'bars of the selected timeframe';
              return (
                <TacticalButton
                  key={m.key}
                  onClick={() => setShowMA((v) => ({ ...v, [m.key]: !v[m.key] }))}
                  className="font-mono"
                  size="sm"
                  variant={showMA[m.key] ? 'primary' : 'ghost'}
                  title={`${m.key === 'ema50' ? 'Exponential' : 'Simple'} moving average over the trailing ${m.period} ${unit}.${short != null ? ` Not drawn: ${short} bars loaded, ${m.period} needed.` : ''}`}
                >
                  <span className="inline-block w-2.5 h-0.5 mr-1.5 align-middle" style={{ background: maColor(m, theme) }} />
                  {m.label}{short != null && showMA[m.key] ? ' · n/a' : ''}
                </TacticalButton>
              );
            })}
            <TacticalButton
              onClick={() => setShowVaR((v) => !v)}
              className="font-mono"
              size="sm"
              variant={showVaR ? 'primary' : 'ghost'}
              disabled={!isDailyTimeframe(timeframe)}
              title="VaR envelope (1-day, historical empirical) — the SAME method as the VaR cards, drawn from the backend, never recomputed here. 95% band (fill) + 99% (dashed); red markers below the close = 99% loss-side VaR exceptions (the model's own backtest); ▲ above = upper-tail breakout, diagnostic only. Daily/EOD only — shown on 3M / 6M / 1Y."
            >
              VaR envelope
            </TacticalButton>
          </div>

        {legendData && (
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mb-2 text-xs font-mono text-cmd-textMuted">
            <span className="text-cmd-textDim">{legendData.time}</span>
            <span>O <span className="text-cmd-text">{formatPrice(legendData.o)}</span></span>
            <span>H <span className="text-cmd-safe">{formatPrice(legendData.h)}</span></span>
            <span>L <span className="text-cmd-critical">{formatPrice(legendData.l)}</span></span>
            <span>C <span className={legendData.c >= legendData.o ? 'text-cmd-safe' : 'text-cmd-critical'}>{formatPrice(legendData.c)}</span></span>
            {legendData.var95l != null && legendData.var99l != null && (
              <>
                <span className="text-[color:rgb(var(--c-gold-muted-rgb)/0.9)]">VaR95 [{formatPrice(legendData.var95l)} … {formatPrice(legendData.var95u!)}]</span>
                <span className="text-[color:rgb(var(--c-gold-muted-rgb)/var(--alpha-text-65))]">VaR99 [{formatPrice(legendData.var99l)} … {formatPrice(legendData.var99u!)}]</span>
              </>
            )}
          </div>
        )}

        {/* Owner 2026-10-05: the chart plot itself stays dark in every theme; the surrounding block follows the theme. */}
        <DarkIsland>
        <div className="relative rounded-[2px] overflow-hidden border border-[color:rgb(var(--c-border-rgb)/0.95)] bg-cmd-surface">
          {loading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-cmd-surface/80">
              <div className="flex items-center gap-3 text-cmd-textMuted font-mono text-sm">
                <div className="w-5 h-5 border-2 border-[color:rgb(var(--c-border-rgb)/0.95)] border-t-cmd-active rounded-full animate-spin" />
                Loading chart data...
              </div>
            </div>
          )}
          {error && !loading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-cmd-surface/90">
              <p className="text-cmd-textMuted font-mono text-sm">{error}</p>
            </div>
          )}
          {/* VaR quality gate — absence is announced, never silent. */}
          {varMeta && varMeta.gate === 'WITHHELD' && (
            <div className="absolute top-0 left-0 right-0 z-[11] px-4 py-2.5 bg-[color:rgb(var(--c-panel-alt-rgb)/0.94)] border-b border-[color:var(--c-hex-9d2b2b)] text-[color:var(--c-red-300)] font-mono text-xs">
              VaR envelope withheld: evidence gate did not clear · reason: {varMeta.reason || 'insufficient return history'}
            </div>
          )}
          {varMeta && varMeta.gate === 'INDICATIVE' && (
            <div className="absolute top-2.5 right-3.5 z-[11] text-[11px] font-mono tracking-[0.1em] text-[color:rgb(var(--c-gold-muted-rgb)/0.85)]">
              INDICATIVE · data quality below threshold
            </div>
          )}
          <div ref={chartContainerRef} style={{ height: 520 }} />
        </div>
        </DarkIsland>
        {varMeta && varMeta.gate !== 'WITHHELD' && (
          <div className="mt-2 text-[11px] font-mono text-cmd-textMuted leading-relaxed break-words">{varMeta.receipt}</div>
        )}
        {varMeta && varMeta.gate !== 'WITHHELD' && varInView && (
          <div className="mt-0.5 text-[11px] font-mono text-cmd-textDim leading-relaxed break-words">{varInView} · viewport count, not a recalibration — run figures above are fixed</div>
        )}
        {varMeta && varMeta.gate !== 'WITHHELD' && (
          <div className="mt-1.5">
            <HowToRead explainKey="var" label="How to read VaR" />
          </div>
        )}

        <div className="mt-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 text-[10px] text-cmd-textDim font-mono uppercase tracking-[0.08em]">
          <span>
            <CoinGeckoAttribution className="normal-case tracking-normal text-cmd-textMuted hover:text-cmd-text" />{' '}· OHLC API · Chart: Lightweight Charts
            {!isDailyTimeframe(timeframe) && (
              <span className="text-cmd-textMuted normal-case tracking-normal"> · switch to 3M / 6M / 1Y to overlay the VaR envelope</span>
            )}
          </span>
          <span className="flex items-center gap-4">
            {MA_LINES.filter((m) => showMA[m.key]).map((m) => (
              <span key={m.key} className="flex items-center gap-1.5">
                <span className="w-3 h-0.5 rounded-[2px]" style={{ background: maColor(m, theme) }} /> {m.label}
              </span>
            ))}
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 bg-[color:rgb(var(--c-safe-rgb)/0.45)] rounded-[2px]" /> Vol ↑
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 bg-[color:rgb(var(--c-critical-rgb)/0.38)] rounded-[2px]" /> Vol ↓
            </span>
          </span>
        </div>
        <ProvenanceFooter
          source="CoinGecko OHLC API"
          method="Lightweight Charts · SMA/EMA computed server-side (desk parity)"
          asOf={lastUpdated}
        />
        </ChartFrame>
      </div>
    </section>
  );
};

export default PriceChartWidget;
